import { useState } from 'react'
import { CalendarPlus, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { TaskAttachments } from '@/components/TaskAttachments'
import { formatDay, type TaskStage, type TaskView } from '@/lib/tasks-board'
import { agendaOf, EMPTY_AGENDA, TaskFormValues, toUpdatePayload, valuesFromTask } from '@/lib/task-form'
import { taskService } from '@/services/task-service'
import { taskBoardService, type Carteira, type WaitingInput } from '@/services/task-board-service'
import { apiErrorMessage, type TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'
import { TaskAlertBadge, TaskExtraBadges, TaskPriorityBadge } from '../board/TaskBadges'
import { TaskAgendaFields } from './TaskAgendaFields'
import { TaskComments } from './TaskComments'
import { TaskForm } from './TaskForm'
import { TaskStageControl } from './TaskStageControl'

interface TaskDetailsPanelProps {
  task: TaskView
  members: TeamMember[]
  carteiras: Carteira[]
  onChanged: (task?: TaskView) => void
}

/** Ver/editar uma tarefa: etapa, campos, compromisso de agenda, anexos e histórico. */
export function TaskDetailsPanel({ task, members, carteiras, onChanged }: TaskDetailsPanelProps) {
  const [busy, setBusy] = useState(false)
  const [agenda, setAgenda] = useState({ ...EMPTY_AGENDA, enabled: true })
  const [showAgenda, setShowAgenda] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const run = async (action: () => Promise<TaskView | void>, done?: string) => {
    setBusy(true)
    try {
      const updated = await action()
      if (done) toast.success(done)
      setHistoryKey((k) => k + 1)
      onChanged(updated ?? undefined)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível salvar a tarefa'))
    } finally {
      setBusy(false)
    }
  }

  const move = (stage: TaskStage, waiting?: WaitingInput) => run(async () => {
    const result = await taskBoardService.moveStage(task.id, stage, waiting)
    if (result.enviada_para_revisao) toast.info('Tarefa com revisor: enviada para revisão')
    return result.task
  })

  const save = (values: TaskFormValues) => {
    const patch = toUpdatePayload(values, task)
    if (Object.keys(patch).length === 0) return
    return run(async () => (await taskService.update(task.id, patch)).data.task as TaskView, 'Tarefa salva')
  }

  const requestAgenda = () => {
    const request = agendaOf({ ...valuesFromTask(task), agenda })
    if (!request?.data) {
      toast.error('Informe a data do compromisso')
      return
    }
    return run(async () => {
      await taskBoardService.requestAgenda(task.id, request)
      setShowAgenda(false)
    }, 'Compromisso pedido à agenda')
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <TaskPriorityBadge task={task} />
        <TaskAlertBadge task={task} />
        <TaskExtraBadges task={task} />
        <span className="text-xs text-muted-foreground">
          Prazo interno {formatDay(task.prazo_interno ?? task.due_date)}
          {task.prazo_fatal ? ` · fatal ${formatDay(task.prazo_fatal)}` : ''}
        </span>
      </div>

      <TaskStageControl
        key={`${task.id}-${task.etapa}`}
        task={task}
        busy={busy}
        onMove={move}
        onReopen={() => run(() => taskBoardService.reopen(task.id), 'Tarefa reaberta')}
      />

      <Separator />
      <TaskForm
        key={`${task.id}-${task.updated_at}`}
        initial={valuesFromTask(task)}
        members={members}
        carteiras={carteiras}
        mode="edit"
        saving={busy}
        onSubmit={save}
      />

      {task.attachment_url && (
        <a className="flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline" href={task.attachment_url} target="_blank" rel="noreferrer">
          <ExternalLink className="size-4" /> Abrir anexo
        </a>
      )}

      <Separator />
      {task.linked_event_id ? (
        <p className="text-sm text-muted-foreground">Compromisso ligado na agenda.</p>
      ) : showAgenda ? (
        <div className="flex flex-col gap-2">
          <TaskAgendaFields value={agenda} onChange={(next) => setAgenda({ ...next, enabled: true })} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowAgenda(false)}>Cancelar</Button>
            <Button size="sm" disabled={busy} onClick={requestAgenda}>Criar compromisso</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="self-start" onClick={() => setShowAgenda(true)}>
          <CalendarPlus className="size-4" /> Compromisso na agenda
        </Button>
      )}

      <Separator />
      <TaskAttachments taskId={String(task.id)} />
      <TaskComments taskId={task.id} refreshKey={historyKey} />
    </div>
  )
}
